import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "react-toastify";
import ContentBox from "../components/ContentBox";
import HabboImage from "../components/HabboImage";
import { fetchProfileColor, getAvatarBgStyle, isAvatarBgImage, safeHexOrFallback } from "../services/profileColorService";
import {
    loadEmoteContext,
    useEmoteContext,
    renderEmotesInHtmlMemo,
} from "../services/emoteService";
import { getSkins } from "../data/profileSkins";
import PostItem from "../components/profile/PostItem";
import PostThreadModal from "../components/PostThreadModal";
import { getFranklyCloudItemsForUser, getAppreciationWall } from "../services/api";
import { decodeCloudItem } from "../utils/cloudItemUtils";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const PAGE_SIZE        = 20;
const POLL_INTERVAL_MS = 300_000;
const API_URL          = process.env.REACT_APP_API_URL;

// Feed definitions – drives the dropdown and column rendering
const FEEDS = [
    { key: "recent",    label: "Newest Posts",     badge: "Live feed",      accent: "#3b82f6", headerBg: "#eff6ff" },
    { key: "hour",      label: "Hot This Hour",     badge: "🔥 Past hour",  accent: "#f97316", headerBg: "#fff7ed" },
    { key: "discussed", label: "Most Discussed",    badge: "💬 Hot threads", accent: "#a855f7", headerBg: "#faf5ff" },
    { key: "all",       label: "Most Liked",        badge: "🏆 All time",   accent: "#f59e0b", headerBg: "#fffbeb" },
];

// ---------------------------------------------------------------------------
// Module-level cache (Bug 1 fix: poll now invalidates before fetching)
// ---------------------------------------------------------------------------
const cache    = new Map();
const inFlight = new Map();

const cachedFetch = async (url, ttlMs = 30_000) => {
    const now    = Date.now();
    const cached = cache.get(url);
    if (cached && now - cached.ts < ttlMs) return cached.data;

    const existing = inFlight.get(url);
    if (existing) return existing;

    const controller = new AbortController();
    const timeoutId  = setTimeout(() => controller.abort(), 15_000);

    const p = fetch(url, { credentials: "include", signal: controller.signal })
        .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
        .then(data => { cache.set(url, { ts: Date.now(), data }); return data; })
        .finally(() => { clearTimeout(timeoutId); inFlight.delete(url); });

    inFlight.set(url, p);
    return p;
};

// Only invalidates the specific feed prefix, not the entire cache
// (Bug 3 fix: was nuking all /top-posts/ on every column simultaneously)
const invalidateFeed = (feedKey) => {
    const prefixMap = {
        recent:    "/top-posts/posts/recent",
        hour:      "/top-posts/posts/top?period=hour",
        discussed: "/top-posts/posts/most-commented",
        all:       "/top-posts/posts/top?period=all",
        luv:       "/top-posts/posts/luv",
    };
    const prefix = prefixMap[feedKey] || "/top-posts/posts";
    for (const key of [...cache.keys()]) {
        if (key.includes(prefix)) cache.delete(key);
    }
};

const invalidateAllFeeds = () => {
    for (const key of [...cache.keys()]) {
        if (key.includes("/top-posts/posts")) cache.delete(key);
    }
};

// Module-level cloud items cache - keyed by author name, 5 min TTL.
// Shared across all feed panels so switching feeds doesn't re-fetch.
const cloudAuthorCache = new Map();
const CLOUD_CACHE_TTL_MS = 5 * 60 * 1000;
const cloudInFlight = new Map();

const getCloudForAuthor = (author, resolveUpload) => {
    const cached = cloudAuthorCache.get(author);
    if (cached && Date.now() - cached.ts < CLOUD_CACHE_TTL_MS) return Promise.resolve(cached.map);
    if (cloudInFlight.has(author)) return cloudInFlight.get(author);
    const p = getFranklyCloudItemsForUser(author)
        .then(async data => {
            if (!data?.success || !Array.isArray(data.items)) return {};
            const m = {};
            await Promise.all(data.items.map(async item => {
                const decoded = item.type === 'upload' ? { ...item, src: resolveUpload(item.src) } : await decodeCloudItem(item);
                m[decoded.id] = decoded;
            }));
            cloudAuthorCache.set(author, { ts: Date.now(), map: m });
            return m;
        })
        .catch(() => ({}))
        .finally(() => cloudInFlight.delete(author));
    cloudInFlight.set(author, p);
    return p;
};

// Parse a post's content for cloud image refs and return the author if any are found.
const postHasCloudRefs = (post) => {
    const raw = String(post?.content ?? post?.Content ?? '');
    const match = raw.match(/data-images='([^']*)'/);
    if (!match) return false;
    try {
        const refs = JSON.parse(match[1].replace(/&#39;/g, "'"));
        return refs.some(r => r.id != null);
    } catch { return false; }
};

// ---------------------------------------------------------------------------
// Post field helpers
// ---------------------------------------------------------------------------
const getPostId     = (p) => String(p?.id ?? p?.ID ?? "");
const getLikesCount = (p) => { const n = Number(p?.likesCount ?? p?.likes_count ?? p?.likes ?? 0); return Number.isFinite(n) ? n : 0; };
const getIsLiked    = (p) => {
    const v = p?.isLikedByViewer ?? p?.is_liked_by_viewer ?? p?.viewerHasLiked;
    if (typeof v === "boolean") return v;
    if (typeof v === "number")  return v > 0;
    if (typeof v === "string")  return v === "1" || v.toLowerCase() === "true";
    return null;
};

const timeAgo = (ts) => {
    if (!ts) return "";
    const s = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
    if (s < 60)  return `${s}s`;
    const m = Math.floor(s / 60);
    if (m < 60)  return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24)  return `${h}h`;
    return `${Math.floor(h / 24)}d`;
};

const decodeEntities = (s) =>
    String(s ?? "").replace(/&#(\d+);/g, (_, n) => String.fromCodePoint(Number(n)));

// ---------------------------------------------------------------------------
// HTML / URL / mention helpers (unchanged from original)
// ---------------------------------------------------------------------------
const URL_REGEX     = /\b((https?:\/\/|www\.)[^\s<]+[^\s<\.)])/gi;
const MENTION_REGEX = /(^|[\s(])@([A-Za-z0-9_]{2,32})\b/g;

const isSafeExternalUrl = (url) => {
    try { const u = new URL(url); return u.protocol === "http:" || u.protocol === "https:"; }
    catch { return false; }
};

const getUrlPageName = (url) => {
    try {
        const u = new URL(url);
        const parts = u.pathname.split("/").filter(Boolean);
        const last = parts[parts.length - 1] || u.hostname;
        const name = decodeURIComponent(last).replace(/[-_+]/g, " ").replace(/\.[^.]+$/, "");
        return name.slice(0, 15).trim() || u.hostname.slice(0, 15);
    } catch {
        return url.slice(0, 15);
    }
};

const isSafeUrl = (url) => {
    try { const u = new URL(url); return u.protocol === "http:" || u.protocol === "https:"; }
    catch { return false; }
};

const externalizeAndMentionHtml = (rawHtml, { mentionHrefForName } = {}) => {
    const input = String(rawHtml ?? "");
    if (!input) return "";
    const parser = new DOMParser();
    const doc    = parser.parseFromString(`<div>${input}</div>`, "text/html");
    const root   = doc.body.firstChild;

    const unwrapAnchors = (node) => {
        if (!node) return;
        (node.querySelectorAll ? node.querySelectorAll("a") : []).forEach(a => {
            a.replaceWith(doc.createTextNode(a.textContent || ""));
        });
    };
    unwrapAnchors(root);

    const replaceInTextNode = (textNode) => {
        const text = textNode.nodeValue || "";
        if (!text) return;
        const parts = [];
        let lastIndex = 0;

        const pushText    = (s) => { if (s) parts.push(doc.createTextNode(s)); };
        const pushExternal = (url) => {
            const link = doc.createElement("a");
            link.textContent = getUrlPageName(String(url || ""));
            link.setAttribute("href", "#");
            link.setAttribute("data-external-url", String(url || ""));
            link.setAttribute("data-external", "1");
            link.className = "text-blue-700 hover:underline font-bold";
            parts.push(link);
        };
        const pushMention = (fullPrefix, name) => {
            const href = typeof mentionHrefForName === "function" ? mentionHrefForName(name) : null;
            if (!href) { pushText(`${fullPrefix}@${name}`); return; }
            pushText(fullPrefix);
            const a = doc.createElement("a");
            a.textContent = `@${name}`;
            a.setAttribute("href", href);
            a.className = "text-blue-700 hover:underline font-bold";
            a.setAttribute("data-mention", "1");
            parts.push(a);
        };

        const matches = [];
        let m;
        URL_REGEX.lastIndex = 0;
        while ((m = URL_REGEX.exec(text)) !== null)
            matches.push({ type: "url", index: m.index, len: m[0].length, value: m[0] });
        MENTION_REGEX.lastIndex = 0;
        while ((m = MENTION_REGEX.exec(text)) !== null) {
            const prefix = m[1] || "", name = m[2] || "";
            matches.push({ type: "mention", index: m.index, len: `${prefix}@${name}`.length, prefix, name });
        }
        if (matches.length === 0) return;
        matches.sort((a, b) => a.index - b.index);
        const filtered = []; let cursor = -1;
        for (const item of matches) {
            if (item.index < cursor) continue;
            filtered.push(item); cursor = item.index + item.len;
        }
        for (const item of filtered) {
            pushText(text.slice(lastIndex, item.index));
            if (item.type === "url") {
                let url = item.value;
                if (/^www\./i.test(url)) url = `https://${url}`;
                if (isSafeExternalUrl(url)) pushExternal(url); else pushText(item.value);
            } else if (item.type === "mention") {
                pushMention(item.prefix, item.name);
            }
            lastIndex = item.index + item.len;
        }
        pushText(text.slice(lastIndex));
        const frag = doc.createDocumentFragment();
        parts.forEach(n => frag.appendChild(n));
        textNode.replaceWith(frag);
    };

    const walk = (node) => {
        if (!node) return;
        if (node.nodeType === Node.TEXT_NODE)    { replaceInTextNode(node); return; }
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        Array.from(node.childNodes).forEach(walk);
    };
    walk(root);
    return root.innerHTML || "";
};

// ---------------------------------------------------------------------------
// Colour helpers
// ---------------------------------------------------------------------------
const hexToRgb = (hex) => {
    const h = String(hex || "").trim();
    if (!/^#[0-9A-Fa-f]{6}$/.test(h)) return { r: 231, g: 35, b: 84 };
    return { r: parseInt(h.slice(1, 3), 16), g: parseInt(h.slice(3, 5), 16), b: parseInt(h.slice(5, 7), 16) };
};

const hexToRgbObj = (hex) => {
    const h = String(hex || "").trim();
    if (!/^#[0-9A-Fa-f]{6}$/.test(h)) return null;
    return { r: parseInt(h.slice(1, 3), 16), g: parseInt(h.slice(3, 5), 16), b: parseInt(h.slice(5, 7), 16) };
};

const rgbToHex = ({ r, g, b }) =>
    `#${[r, g, b].map(v => Math.max(0, Math.min(255, v)).toString(16).padStart(2, "0")).join("")}`;

const mixWithWhite = (hex, amount = 0.95) => {
    const c = hexToRgbObj(hex);
    if (!c) return "#f7f7f7";
    const a = Math.max(0, Math.min(1, amount));
    return rgbToHex({ r: Math.round(c.r + (255 - c.r) * a), g: Math.round(c.g + (255 - c.g) * a), b: Math.round(c.b + (255 - c.b) * a) });
};

const svgIdSafe = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9_-]/g, "_");

// ---------------------------------------------------------------------------
// Heart skin helpers (mirrors LuvBox logic)
// ---------------------------------------------------------------------------
const _assetMatchesHeart = (a) => {
    const p = a.placement;
    if (Array.isArray(p)) return p.includes('heart');
    return typeof p === 'string' && (p === 'heart' || p.includes('heart'));
};
const getHeartAssets = (skin) =>
    skin && skin.id !== 'default' ? (skin.assets || []).filter(_assetMatchesHeart) : [];

// ---------------------------------------------------------------------------
// API fetchers
// ---------------------------------------------------------------------------
const DEFAULT_LUV_HEART_COLOUR = "#e72354";

const fetchPosts = {
    recent:    (limit, offset) => cachedFetch(`${API_URL}/top-posts/posts/recent?limit=${limit}&offset=${offset}`, 20_000),
    all:       (limit, offset) => cachedFetch(`${API_URL}/top-posts/posts/top?period=all&limit=${limit}&offset=${offset}`, 60_000),
    hour:      (limit, offset) => cachedFetch(`${API_URL}/top-posts/posts/top?period=hour&limit=${limit}&offset=${offset}`, 15_000),
    luv:       (limit, offset) => cachedFetch(`${API_URL}/top-posts/posts/luv?limit=${limit}&offset=${offset}`, 20_000),
    discussed: (limit, offset) => cachedFetch(`${API_URL}/top-posts/posts/most-commented?limit=${limit}&offset=${offset}`, 30_000),
};

// ---------------------------------------------------------------------------
// useColumn hook  (Bug 1 fix: poll invalidates cache before fetching)
//                 (Bug 3 fix: only invalidates own feed, not all feeds)
// ---------------------------------------------------------------------------
const useColumn = ({ feedKey, fetcher, pollMs = POLL_INTERVAL_MS, errorMsg, refreshSignal = 0 }) => {
    const [posts,       setPosts]       = useState([]);
    const [loading,     setLoading]     = useState(true);
    const [refreshing,  setRefreshing]  = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore,     setHasMore]     = useState(true);
    const [offset,      setOffset]      = useState(0);
    const [lastRefreshed, setLastRefreshed] = useState(Date.now());
    const aliveRef = useRef(true);

    const load = useCallback(async (nextOffset, { append = false, silent = false } = {}) => {
        try {
            const res  = await fetcher(PAGE_SIZE, nextOffset);
            const next = Array.isArray(res?.posts) ? res.posts : [];
            if (aliveRef.current) {
                setPosts(prev => append ? [...prev, ...next] : next);
                setOffset(nextOffset);
                setHasMore(next.length === PAGE_SIZE);
            }
        } catch (err) {
            if (!silent) toast.error(errorMsg || "Failed to load posts.");
            throw err;
        }
    }, [fetcher, errorMsg]);

    // Initial load
    useEffect(() => {
        aliveRef.current = true;
        setLoading(true);
        load(0).finally(() => { if (aliveRef.current) setLoading(false); });
        return () => { aliveRef.current = false; };
    }, [load]);

    // Auto-poll - FIX (Bug 1): invalidate this feed's cache entries before
    // fetching so the poll actually gets fresh data instead of the cached copy.
    useEffect(() => {
        const timer = setInterval(async () => {
            if (!aliveRef.current) return;
            try {
                invalidateFeed(feedKey);
                await load(0, { silent: true });
                if (aliveRef.current) setLastRefreshed(Date.now());
            } catch {}
        }, pollMs);
        return () => clearInterval(timer);
    }, [load, pollMs, feedKey]);

    // WebSocket-driven refresh - FIX (Bug 3): only invalidate this feed's
    // cache, not everything, so sibling columns aren't disrupted.
    useEffect(() => {
        if (refreshSignal === 0 || !aliveRef.current) return;
        invalidateFeed(feedKey);
        load(0, { silent: true }).then(() => {
            if (aliveRef.current) setLastRefreshed(Date.now());
        }).catch(() => {});
    }, [refreshSignal, load, feedKey]);

    const refresh = useCallback(async () => {
        invalidateFeed(feedKey);
        setRefreshing(true);
        try { await load(0); if (aliveRef.current) setLastRefreshed(Date.now()); }
        catch {}
        finally { if (aliveRef.current) setRefreshing(false); }
    }, [load, feedKey]);

    const loadMore = useCallback(async () => {
        if (!hasMore || loadingMore) return;
        setLoadingMore(true);
        try { await load(offset + PAGE_SIZE, { append: true }); }
        catch {}
        finally { if (aliveRef.current) setLoadingMore(false); }
    }, [load, hasMore, loadingMore, offset]);

    return { posts, loading, refreshing, loadingMore, hasMore, lastRefreshed, refresh, loadMore };
};

// ---------------------------------------------------------------------------
// External link modal (unchanged)
// ---------------------------------------------------------------------------
const ExternalLinkModal = ({ url, onClose, onContinue }) => {
    if (!url) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
            <div className="w-full max-w-sm border border-black/40 bg-offwhite rounded shadow-lg overflow-hidden">
                <div className="px-3 py-2 border-b border-black/20 font-bold text-coal text-sm">External Link</div>
                <div className="p-3 text-sm text-coal">
                    <p className="mb-2">You are about to leave Frankly and visit an external site:</p>
                    <p className="break-all font-mono text-xs bg-black/5 px-2 py-1 rounded border border-black/10">{url}</p>
                </div>
                <div className="p-2 border-t border-black/20 flex justify-end gap-2 bg-black/5">
                    <button type="button" onClick={onClose} className="px-3 py-1 border border-black/30 bg-offwhite text-coal text-sm rounded hover:bg-black/5">Cancel</button>
                    <button type="button" onClick={onContinue} className="px-3 py-1 border border-black/30 bg-gold text-coal text-sm rounded hover:brightness-95">Continue</button>
                </div>
            </div>
        </div>
    );
};


// ---------------------------------------------------------------------------
// RefreshIcon (unchanged)
// ---------------------------------------------------------------------------
const RefreshIcon = ({ spinning }) => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={spinning ? "animate-spin" : ""}>
        <polyline points="23 4 23 10 17 10"/>
        <path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/>
    </svg>
);


// ---------------------------------------------------------------------------
// LuvRow - identical to original.
// FIX (Bug 4): removed Math.random() from tintId useMemo - the random suffix
// caused SVG filter IDs to mismatch on re-render, making heart tints flash.
// ---------------------------------------------------------------------------
const LuvRow = ({ post, luvById }) => {
    const navigate = useNavigate();
    const author   = String(post.habboName || "Unknown");

    const luvState = luvById.get(author) || {};
    const luvCount = luvState.count ?? (Number(post.luvCount) || 0);

    const base  = post.luvHeartColour || DEFAULT_LUV_HEART_COLOUR;
    // FIX: stable ID - no Math.random()
    const tintId = React.useMemo(() => `luvTint_tp_${svgIdSafe(author)}`, [author]);

    const activeSkin       = React.useMemo(() => getSkins().find(s => s.id === post.luvHeartSkin) ?? null, [post.luvHeartSkin]);
    const heartAssets      = React.useMemo(() => getHeartAssets(activeSkin), [activeSkin]);
    const heartBehindLayers  = heartAssets.filter(a => a.heartDepth === 'behind');
    const heartInfrontLayers = heartAssets.filter(a => a.heartDepth !== 'behind');

    const { r, g, b } = hexToRgb(base);
    const mr = (r / 255).toFixed(6), mg = (g / 255).toFixed(6), mb = (b / 255).toFixed(6);

    const goProfile = () => navigate(`/members/profile/${encodeURIComponent(author)}`);

    const [avatarBg, setAvatarBg] = useState(() => String(post?.profileBg || post?.profile_bg || '#5A2929'));
    useEffect(() => {
        let cancelled = false;
        fetchProfileColor(author).then(c => { if (!cancelled) setAvatarBg(c); });
        return () => { cancelled = true; };
    }, [author]);

    // When avatarBg is an image URL we can't use it for colour maths.
    // Use the heart colour (base) for row tint and drop-shadow instead.
    const tintHex = isAvatarBgImage(avatarBg) ? base : safeHexOrFallback(avatarBg);

    return (
        <div className="border-b relative border-black/10 last:border-b-0 flex flex-col h-[110px]" style={{ backgroundColor: mixWithWhite(tintHex, 0.6) }}>
            <div className="flex gap-2.5 flex-1 min-h-0 overflow-hidden">
                <div
                    className="w-[80px] h-full border pr-6 border-black/10 shrink-0 overflow-hidden flex items-center justify-center cursor-pointer hover:brightness-95"
                    style={getAvatarBgStyle(avatarBg)}
                    onClick={goProfile}
                    title={`Visit ${author}'s profile`}
                >
                    <HabboImage habbo={author} hotel="COM" size="a" direction={2} head_direction={2} gesture="sml" headonly="true" />
                </div>
                <div className="flex-1 min-w-0 flex flex-col justify-center gap-2">
                    <div className="flex mt-2 justify-between items-center gap-2 relative left-10">
                        <span className="font-bold text-coal text-xs cursor-pointer hover:underline truncate" onClick={goProfile}>{author} got luv'd!</span>
                    </div>
                    <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true" focusable="false">
                        <filter id={tintId} colorInterpolationFilters="sRGB">
                            <feColorMatrix type="matrix" values={`${mr} 0 0 0 0  0 ${mg} 0 0 0  0 0 ${mb} 0 0  0 0 0 1 0`} />
                        </filter>
                    </svg>
                    <div className="flex items-center gap-3 relative -left-6 -top-3.5 z-10">
                        <div className="w-[29px] h-[28px] -top-0.5" style={{ position: "relative", display: "inline-block", left: "-1px" }}>
                            {heartBehindLayers.map((asset, i) => (
                                <img key={i} src={asset.image} alt="" draggable={false} style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", pointerEvents: "none", maxWidth: "none", zIndex: 1, imageRendering: "pixelated" }} />
                            ))}
                            <img src="/img/luv_heart_lg.png" alt="" style={{ display: "block", filter: `url(#${tintId}) drop-shadow(0 0 4px ${mixWithWhite(tintHex, 0.6)}) drop-shadow(0 0 8px ${mixWithWhite(tintHex, 0.75)})`, imageRendering: "pixelated", width: 29, height: 28, position: "relative", zIndex: 2, scale: 2 }} />
                            {heartInfrontLayers.length > 0 ? (
                                heartInfrontLayers.map((asset, i) => (
                                    <img key={i} src={asset.image} alt="" draggable={false} style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", pointerEvents: "none", maxWidth: "none", zIndex: 3, imageRendering: "pixelated" }} />
                                ))
                            ) : (
                                <img src="/img/luv_heart_lg_sparkle.png" alt="" style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none", imageRendering: "pixelated", width: 29, height: 28, zIndex: 3, scale: 2 }} />
                            )}
                        </div>
                        <div className="flex flex-col items-left left-6 h-[50px] z-30 relative">
                            <div className="font-['Jersey_10'] text-5xl leading-none text-coal tabular-nums">{luvCount.toLocaleString()}</div>
                            <div className="font-['Jersey_10'] relative -top-2 text-coal/60 uppercase tracking-widest leading-none">Total Luv</div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="px-2.5 py-1 border-t border-black/5 text-[9px] text-coal/40 flex items-center justify-between cursor-pointer hover:bg-black/5 transition-colors" style={{ backgroundColor: "#e9e9e9" }} onClick={goProfile}>
                <span className="truncate">Visit {author}&apos;s profile to give them luv!</span>
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// ColumnShell - wraps a scrollable feed with header, refresh button, countdown
// ---------------------------------------------------------------------------
const ColumnShell = ({ title, badge, headerBg, accent, loading, refreshing, empty, emptyText, onRefresh, lastRefreshed, pollMs = 300_000, pinned, children }) => {
    const [countdown, setCountdown] = useState(Math.round(pollMs / 1000));
    const [cooldown,  setCooldown]  = useState(0);

    useEffect(() => {
        setCountdown(Math.round(pollMs / 1000));
        const t = setInterval(() => setCountdown(p => Math.max(0, p - 1)), 1000);
        return () => clearInterval(t);
    }, [lastRefreshed]);

    useEffect(() => {
        if (cooldown <= 0) return;
        const t = setInterval(() => setCooldown(p => Math.max(0, p - 1)), 1000);
        return () => clearInterval(t);
    }, [cooldown]);

    const handleRefresh = useCallback(() => {
        if (loading || refreshing || cooldown > 0) return;
        setCooldown(3);
        onRefresh();
    }, [loading, refreshing, cooldown, onRefresh]);

    return (
        <div className="flex flex-col border border-black/20 overflow-hidden h-full">
            <div className="px-2.5 py-2 border-b border-black/20 flex items-center justify-between shrink-0 gap-2" style={{ backgroundColor: headerBg }}>
                <div className="flex items-center gap-1.5 min-w-0">
                    <span className="font-bold text-coal text-sm truncate">{title}</span>
                    {badge && (
                        <span className="text-[9px] font-bold text-coal/50 bg-white/60 border border-black/10 rounded px-1.5 py-0.5 shrink-0 hidden sm:block">{badge}</span>
                    )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                    <span className="text-[9px] text-coal/30 font-mono">{cooldown > 0 ? `${cooldown}s` : `${countdown}s`}</span>
                    <button
                        type="button"
                        onClick={handleRefresh}
                        disabled={refreshing || loading || cooldown > 0}
                        title="Refresh"
                        className="text-coal/50 hover:text-coal transition-colors disabled:opacity-40"
                    >
                        <RefreshIcon spinning={refreshing || loading} />
                    </button>
                </div>
            </div>
            {pinned && <div className="shrink-0">{pinned}</div>}
            <div className="overflow-y-auto habbo-scroll bg-offwhite flex-1">
                {loading ? (
                    <div className="flex items-center justify-center text-coal/40 text-xs p-6 animate-pulse h-full min-h-[200px]">Loading…</div>
                ) : empty ? (
                    <div className="flex items-center justify-center text-coal/40 text-xs text-center p-6 h-full min-h-[200px]">{emptyText}</div>
                ) : children}
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// LoadMoreRow (unchanged)
// ---------------------------------------------------------------------------
const LoadMoreRow = ({ onClick, loading }) => (
    <div className="p-2 border-t border-black/10 bg-black/5">
        <button type="button" onClick={onClick} disabled={loading} className="w-full px-3 py-1.5 border border-black/20 bg-offwhite text-coal text-xs font-bold rounded hover:bg-black/5 disabled:opacity-60">
            {loading ? "Loading…" : `Load ${PAGE_SIZE} more`}
        </button>
    </div>
);

// ---------------------------------------------------------------------------
// ConfirmModal
// ---------------------------------------------------------------------------
const ConfirmModal = ({ state, onClose }) => {
    if (!state) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
            <div className="w-full max-w-sm border border-black/40 bg-offwhite rounded shadow-lg overflow-hidden">
                <div className="px-3 py-2 border-b border-black/20 font-bold text-coal text-sm">{state.title}</div>
                <div className="p-3 text-sm text-coal">{state.message}</div>
                <div className="p-2 border-t border-black/20 flex justify-end gap-2 bg-black/5">
                    <button type="button" onClick={onClose} className="px-3 py-1 border border-black/30 bg-offwhite text-coal text-sm rounded hover:bg-black/5">Cancel</button>
                    <button type="button" onClick={() => { state.onConfirm(); onClose(); }} className="px-3 py-1 border border-black/30 bg-red-500 text-white text-sm rounded hover:bg-red-600">Confirm</button>
                </div>
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// UnifiedFeedPanel - single feed panel driven by the selected feed key.
// Only one feed is mounted at a time, so only one poll runs.
// ---------------------------------------------------------------------------
const UnifiedFeedPanel = ({ feedKey, userData, ingestPosts, likesById, likedById, refreshSignal, onOpenThread, requestConfirm }) => {
    const navigate = useNavigate();
    const feed    = FEEDS.find(f => f.key === feedKey) || FEEDS[0];
    const fetcher = useCallback((l, o) => fetchPosts[feedKey](l, o), [feedKey]);

    const { posts, loading, refreshing, loadingMore, hasMore, lastRefreshed, refresh, loadMore } =
        useColumn({ feedKey, fetcher, errorMsg: `Failed to load ${feed.label}.`, refreshSignal });

    useEffect(() => { if (ingestPosts) ingestPosts(posts); }, [posts, ingestPosts]);

    const [externalUrl, setExternalUrl] = useState("");

    const [cloudItemsMap, setCloudItemsMap] = useState({});
    const [cloudItemsMapReady, setCloudItemsMapReady] = useState(false);
    // Track authors already resolved so polls don't re-fetch within the same mount.
    const resolvedAuthorsRef = useRef(new Set());

    useEffect(() => {
        let cancelled = false;
        if (!posts.length) { setCloudItemsMapReady(true); return; }
        const apiOrigin = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '').replace(/\/$/, '');
        const resolveUpload = (src) => (!src || src.startsWith('data:') || src.startsWith('http') ? src : `${apiOrigin}${src.startsWith('/') ? '' : '/'}${src}`);
        // Only fetch for authors with cloud refs we haven't loaded yet this mount.
        const authorsNeeded = [...new Set(
            posts
                .filter(postHasCloudRefs)
                .map(p => String(p.habboName || p.authorName || p.username || ''))
                .filter(Boolean)
        )].filter(a => !resolvedAuthorsRef.current.has(a));
        if (authorsNeeded.length === 0) { setCloudItemsMapReady(true); return; }
        Promise.all(authorsNeeded.map(a => getCloudForAuthor(a, resolveUpload)))
            .then(maps => {
                if (cancelled) return;
                authorsNeeded.forEach(a => resolvedAuthorsRef.current.add(a));
                setCloudItemsMap(prev => Object.assign({}, prev, ...maps));
                setCloudItemsMapReady(true);
            });
        return () => { cancelled = true; };
    }, [posts]);

    const likedPostIds = React.useMemo(() => {
        const s = new Set();
        for (const [id, liked] of likedById) { if (liked) s.add(id); }
        return s;
    }, [likedById]);

    return (
        <>
            <ColumnShell
                title={feed.label}
                badge={feed.badge}
                headerBg={feed.headerBg}
                accent={feed.accent}
                loading={loading}
                refreshing={refreshing}
                empty={!loading && posts.length === 0}
                emptyText="Nothing to show here yet - check back soon!"
                onRefresh={refresh}
                lastRefreshed={lastRefreshed}
            >
                {posts.map((post) => {
                    const author = String(post.habboName || post.authorName || post.username || "Unknown");
                    const goProfile = () => navigate(`/members/profile/${encodeURIComponent(author)}`);
                    return (
                        <PostItem
                            key={getPostId(post)}
                            post={post}
                            profileUsername={author}
                            isOwnProfile={false}
                            userData={userData}
                            timeAgo={timeAgo}
                            onDeletePost={() => {}}
                            onBlockUser={() => {}}
                            likedPostIds={likedPostIds}
                            likedCommentIds={new Set()}
                            likeCountsByPostId={likesById}
                            likeCountsByCommentId={{}}
                            requestConfirm={requestConfirm}
                            palette={null}
                            isGuest={!userData}
                            isNarrowColumn={false}
                            cloudItemsMap={cloudItemsMap}
                            cloudItemsMapReady={cloudItemsMapReady}
                            onExternalLink={(url) => setExternalUrl(url)}
                            hideThread={true}
                            footerBar={() => (
                                <>
                                    <div className="self-stretch w-px bg-black/10 shrink-0" />
                                    <button
                                        type="button"
                                        onClick={() => typeof onOpenThread === 'function' && onOpenThread(post, author)}
                                        className="flex-1 px-2 py-1.5 flex items-center gap-1.5 text-xs font-bold text-coal/50 hover:text-coal hover:bg-black/5 transition-colors"
                                    >
                                        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                                        </svg>
                                        Reply on thread ({post.commentCount ?? 0})
                                    </button>
                                    <div className="self-stretch w-px bg-black/10 shrink-0" />
                                    <button
                                        type="button"
                                        onClick={goProfile}
                                        className="px-2 py-1.5 flex items-center gap-1 text-xs text-coal/50 hover:text-coal hover:bg-black/5 transition-colors"
                                    >
                                        Profile
                                        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="ml-0.5">
                                            <path d="M5 12h14M12 5l7 7-7 7"/>
                                        </svg>
                                    </button>
                                </>
                            )}
                        />
                    );
                })}
                {!loading && hasMore && <LoadMoreRow onClick={loadMore} loading={loadingMore} />}
            </ColumnShell>
            <ExternalLinkModal
                url={externalUrl}
                onClose={() => setExternalUrl("")}
                onContinue={() => {
                    const u = externalUrl;
                    setExternalUrl("");
                    if (isSafeUrl(u)) window.open(u, "_blank", "noopener,noreferrer");
                }}
            />
        </>
    );
};

// ---------------------------------------------------------------------------
// LuvColumn - identical API to original, uses its own feedKey for cache ops
// ---------------------------------------------------------------------------
const LuvColumn = ({ ingestPosts, luvById, setLuvById, refreshSignal, userData }) => {
    const fetcher = useCallback((limit, offset) => fetchPosts.luv(limit, offset), []);

    // Track the most recent lastLuvAt timestamp we've already seen so we only
    // toast when a genuinely new Luv entry arrives, not on every poll refresh.
    const lastSeenLuvAtRef = useRef(null);

    const handleIngestPosts = useCallback((posts) => {
        if (ingestPosts) ingestPosts(posts);

        if (userData && posts.length > 0) {
            const viewerName = String(userData.habboName || userData.username || "").toLowerCase();

            if (viewerName) {
                // Find the newest lastLuvAt across the whole fetched list
                const newestTs = posts.reduce((max, p) => {
                    const t = p.lastLuvAt ? new Date(p.lastLuvAt).getTime() : 0;
                    return t > max ? t : max;
                }, 0);

                // On the very first ingest just record the high-water mark so we
                // don't false-fire on initial page load
                if (lastSeenLuvAtRef.current === null) {
                    lastSeenLuvAtRef.current = newestTs;
                } else {
                    // Only look at posts whose lastLuvAt is strictly newer than
                    // what we last saw - these are genuinely fresh Luv events
                    const newPosts = posts.filter(p => {
                        const t = p.lastLuvAt ? new Date(p.lastLuvAt).getTime() : 0;
                        return t > lastSeenLuvAtRef.current;
                    });

                    if (newPosts.some(p => String(p.habboName || "").toLowerCase() === viewerName)) {
                        toast.success("💖 You've been Luv'd! Check the Luv column!", { autoClose: 6000 });
                    }

                    // Advance the high-water mark regardless of whether it was us
                    if (newestTs > lastSeenLuvAtRef.current) {
                        lastSeenLuvAtRef.current = newestTs;
                    }
                }
            }
        }

        setLuvById(prev => {
            const next = new Map(prev);
            for (const p of posts) {
                const author = String(p.habboName || "");
                if (!author) continue;
                // Always update count with latest value from API
                const cur = next.get(author) || {};
                next.set(author, { ...cur, count: Number(p.luvCount) || (cur.count ?? 0) });
            }
            return next;
        });
    }, [ingestPosts, setLuvById, userData]);

    const [externalUrl, setExternalUrl] = useState("");

    const { posts, loading, refreshing, loadingMore, hasMore, lastRefreshed, refresh, loadMore } =
        useColumn({ feedKey: "luv", fetcher, errorMsg: "Failed to load Luv'd posts.", refreshSignal, pollMs: 90_000 });

    useEffect(() => { if (handleIngestPosts) handleIngestPosts(posts); }, [posts, handleIngestPosts]);

    return (
        <>
            <ColumnShell
                title="Recently Luv'd"
                badge="❤️ Trending"
                headerBg="#fff1f2"
                accent="#f43f5e"
                loading={loading}
                refreshing={refreshing}
                empty={!loading && posts.length === 0}
                emptyText="No Luv has been given yet - be the first!"
                onRefresh={refresh}
                lastRefreshed={lastRefreshed}
                pollMs={90_000}
            >
                {posts.map((post) => (
                    <LuvRow
                        key={String(post.habboName || getPostId(post))}
                        post={post}
                        luvById={luvById}
                    />
                ))}
                {!loading && hasMore && <LoadMoreRow onClick={loadMore} loading={loadingMore} />}
            </ColumnShell>
            <ExternalLinkModal
                url={externalUrl}
                onClose={() => setExternalUrl("")}
                onContinue={() => {
                    const u = externalUrl;
                    setExternalUrl("");
                    if (isSafeUrl(u)) window.open(u, "_blank", "noopener,noreferrer");
                }}
            />
        </>
    );
};

const APPRECIATED_POLL_MS = 300_000;
const APPRECIATED_DEFAULT_BG = "#5A2929";

const AppreciatedRow = ({ post }) => {
    const navigate = useNavigate();
    const canVisit = Boolean(post.toIsMember);
    const goProfile = () => { if (canVisit) navigate(`/members/profile/${encodeURIComponent(post.toHabboName)}`); };

    return (
        <div className="flex gap-2 p-2 border-b border-black/10 last:border-b-0">
            <div
                className={`w-[42px] h-[42px] shrink-0 relative overflow-hidden rounded border border-black/25 ${canVisit ? "cursor-pointer hover:brightness-95" : ""}`}
                style={getAvatarBgStyle(post.toAvatarBg || APPRECIATED_DEFAULT_BG)}
                onClick={goProfile}
            >
                <HabboImage
                    habbo={post.toHabboName}
                    hotel={post.toHotel || "COM"}
                    size="a"
                    direction={2}
                    head_direction={3}
                    gesture="sml"
                    headonly
                    className="absolute left-1/2 top-[22px] -translate-x-1/2 -translate-y-1/2 max-w-none"
                    alt={post.toHabboName}
                />
            </div>

            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-1">
                    <span
                        onClick={goProfile}
                        className={`font-bold text-coal text-xs truncate ${canVisit ? "cursor-pointer hover:underline" : ""}`}
                    >
                        {post.toHabboName}
                    </span>
                    <span className="shrink-0 flex items-center gap-1 text-[9px] text-coal/60 font-volterBold">
                        <img src="/img/likeHeart-on.png" style={{ imageRendering: "pixelated" }} alt="" />
                        <span className="pt-[2px]">{post.likeCount}</span>
                    </span>
                </div>

                <p className="text-[11px] leading-snug text-coal/85 break-words line-clamp-3 mt-0.5">
                    {post.message}
                </p>

                <div className="text-[10px] italic text-coal/50 truncate mt-0.5">
                    from {post.isAnonymous || !post.fromHabboName ? "Anonymous" : post.fromHabboName}
                </div>
            </div>
        </div>
    );
};

const AppreciatedColumn = () => {
    const navigate = useNavigate();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [lastRefreshed, setLastRefreshed] = useState(null);

    const load = useCallback(async (isRefresh) => {
        if (isRefresh) setRefreshing(true);
        try {
            const res = await getAppreciationWall(1, "latest");
            if (res?.success) setPosts(Array.isArray(res.posts) ? res.posts : []);
        } catch {
            if (!isRefresh) toast.error("Failed to load the appreciation wall.");
        } finally {
            setLoading(false);
            setRefreshing(false);
            setLastRefreshed(Date.now());
        }
    }, []);

    useEffect(() => { load(false); }, [load]);

    useEffect(() => {
        const timer = setInterval(() => load(true), APPRECIATED_POLL_MS);
        return () => clearInterval(timer);
    }, [load]);

    return (
        <ColumnShell
            title="Appreciated!"
            badge="💌 Kind words"
            headerBg="#fdf2f8"
            accent="#ec4899"
            loading={loading}
            refreshing={refreshing}
            empty={!loading && posts.length === 0}
            emptyText="Nobody has been appreciated yet. Be the first!"
            onRefresh={() => load(true)}
            lastRefreshed={lastRefreshed}
            pollMs={APPRECIATED_POLL_MS}
            pinned={
                <div className="p-2 border-b border-black/20 bg-black/5">
                    <button
                        type="button"
                        onClick={() => navigate("/goodies/appreciate")}
                        className="w-full px-3 py-1.5 border border-black/20 bg-offwhite text-coal text-xs font-bold rounded hover:bg-black/5"
                    >
                        Visit the wall!
                    </button>
                </div>
            }
        >
            {posts.map((post) => (
                <AppreciatedRow key={post.id} post={post} />
            ))}
        </ColumnShell>
    );
};

// ---------------------------------------------------------------------------
// FeedDropdown - styled dropdown to switch the active feed
// ---------------------------------------------------------------------------
const FeedDropdown = ({ activeFeedKey, onChange }) => {
    const active = FEEDS.find(f => f.key === activeFeedKey) || FEEDS[0];
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    return (
        <div ref={ref} className="relative">
            <button
                type="button"
                onClick={() => setOpen(v => !v)}
                className="flex items-center gap-2 px-3 py-1.5 border border-black/20 bg-offwhite hover:bg-black/5 text-coal text-sm font-bold rounded transition-colors"
            >
                <span
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{ backgroundColor: active.accent }}
                />
                {active.label}
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform ${open ? "rotate-180" : ""}`}>
                    <polyline points="6 9 12 15 18 9"/>
                </svg>
            </button>
            {open && (
                <div className="absolute top-full mt-1 left-0 z-30 w-52 border border-black/20 bg-offwhite shadow-lg rounded overflow-hidden">
                    {FEEDS.map(feed => (
                        <button
                            key={feed.key}
                            type="button"
                            onClick={() => { onChange(feed.key); setOpen(false); }}
                            className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors ${feed.key === activeFeedKey ? "bg-black/5 font-bold" : "hover:bg-black/5 font-medium"} text-coal`}
                        >
                            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: feed.accent }} />
                            <span className="flex-1">{feed.label}</span>
                            <span className="text-[9px] text-coal/40 font-normal">{feed.badge}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// TopPosts - root component
// FIX (Bug 2): ingestPosts always overwrites with latest value, no has() guard.
// FIX (Bug 3): WebSocket signal only invalidates the active feed's cache.
// ---------------------------------------------------------------------------
function TopPosts({ userData }) {
    const [activeFeedKey, setActiveFeedKey] = useState("recent");

    // Bug 2 fix: always overwrite with the latest like count from whichever
    // fetch just resolved - removed the `if (!next.has(id))` guard that caused
    // stale counts to persist when multiple feeds had the same post.
    const [likesById, setLikesById] = useState(() => new Map());
    const [likedById, setLikedById] = useState(() => new Map());
    const [luvById,   setLuvById]   = useState(() => new Map());

    // WebSocket-driven refresh signal - one signal for the whole page, but each
    // column only invalidates its own cache prefix (Bug 3 fix, in useColumn).
    const [feedRefreshSignal, setFeedRefreshSignal] = useState(0);
    useEffect(() => {
        const handler = () => setFeedRefreshSignal(s => s + 1);
        window.addEventListener("frankly:feedUpdated", handler);
        return () => window.removeEventListener("frankly:feedUpdated", handler);
    }, []);

    // New-activity banner - shown on all tabs except "recent", since that
    // updates itself. Clicking the banner switches to the newest posts tab.
    const [showNewBanner, setShowNewBanner] = useState(false);
    const prevSignalRef = useRef(0);

    useEffect(() => {
        if (feedRefreshSignal === 0) return;
        if (feedRefreshSignal === prevSignalRef.current) return;
        prevSignalRef.current = feedRefreshSignal;
        if (activeFeedKey !== "recent") setShowNewBanner(true);
    }, [feedRefreshSignal, activeFeedKey]);

    // Auto-dismiss banner after 10 seconds if not clicked
    useEffect(() => {
        if (!showNewBanner) return;
        const t = setTimeout(() => setShowNewBanner(false), 10_000);
        return () => clearTimeout(t);
    }, [showNewBanner]);

    // Luv-specific refresh signal - driven by luv_received WebSocket events
    // and a visibility check on mount/return so we catch Luvs given while
    // the user was on another page.
    const [luvRefreshSignal, setLuvRefreshSignal] = useState(0);
    const luvDebounceRef = useRef(null);

    const triggerLuvRefresh = useCallback(() => {
        // 15s debounce - matches server-side broadcastFeedUpdate cooloff so
        // we never hammer the API even if multiple Luvs arrive rapidly.
        if (luvDebounceRef.current) return;
        luvDebounceRef.current = setTimeout(() => {
            luvDebounceRef.current = null;
            invalidateFeed("luv");
            setLuvRefreshSignal(s => s + 1);
        }, 15_000);
    }, []);

    useEffect(() => {
        // Refresh immediately on mount - catches Luvs given while away
        invalidateFeed("luv");
        setLuvRefreshSignal(s => s + 1);

        // Refresh when the user returns to this tab
        const handleVisibility = () => {
            if (document.visibilityState === "visible") triggerLuvRefresh();
        };
        document.addEventListener("visibilitychange", handleVisibility);

        // Listen for targeted luv_received WebSocket messages dispatched by
        // the app's WebSocket client as a custom window event
        const handleLuvReceived = () => triggerLuvRefresh();
        window.addEventListener("frankly:luvReceived", handleLuvReceived);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibility);
            window.removeEventListener("frankly:luvReceived", handleLuvReceived);
            if (luvDebounceRef.current) clearTimeout(luvDebounceRef.current);
        };
    }, [triggerLuvRefresh]);

    const ingestPosts = useCallback((posts) => {
        if (!Array.isArray(posts) || posts.length === 0) return;
        setLikesById(prev => {
            const next = new Map(prev);
            for (const p of posts) {
                const id = getPostId(p);
                if (!id) continue;
                next.set(id, p.totalLikesCount !== undefined ? Number(p.totalLikesCount) : getLikesCount(p));
            }
            return next;
        });
        setLikedById(prev => {
            const next = new Map(prev);
            for (const p of posts) {
                const id = getPostId(p);
                if (!id) continue;
                const v = getIsLiked(p);
                if (v !== null) next.set(id, v);
            }
            return next;
        });
    }, []);

    const [modalPost, setModalPost] = useState(null); // { post, author }
    const openThread = useCallback((post, author) => setModalPost({ post, author }), []);
    const closeThread = useCallback(() => setModalPost(null), []);

    const [confirmState, setConfirmState] = useState(null);
    const requestConfirm = useCallback(({ title, message, onConfirm }) => {
        setConfirmState({ title, message, onConfirm });
    }, []);

    const sharedFeedProps = { userData, ingestPosts, likesById, likedById, refreshSignal: feedRefreshSignal, onOpenThread: openThread, requestConfirm };

    if (!userData) return <Navigate to="/" replace />;

    return (
        <>
        <div className="w-full mx-auto py-6 px-5">
            <style>{`
                .habbo-scroll::-webkit-scrollbar        { width: 14px; }
                .habbo-scroll::-webkit-scrollbar-track  { background: #cfcfcf; border: 1px solid #000; }
                .habbo-scroll::-webkit-scrollbar-thumb  { background: #8f8f8f; border: 1px solid #000; }
                .habbo-scroll::-webkit-scrollbar-thumb:hover { background: #7a7a7a; }
            `}</style>

            <ContentBox header="🔥 What's hot?! Community Interactions!" tint="#d4af37" className="mb-5">
                <div className="text-sm text-coal/70">
                    The Community Posts section lets you see everyone's posts and conversations from across Frankly. You can view the past week's posts, the past week's most commented posts, the recently liked posts and the most liked posts of all time. You'll also see who's getting all the Luv! Click to open a posts thread and reply directly, or visit their profile from their Bio box and enjoy all they have to offer!
                </div>
            </ContentBox>

            {/* Main layout */}
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_280px] gap-3 items-start">

                {/* Left */}
                <div className="relative flex flex-col border border-black/20 overflow-hidden" style={{ height: 800 }}>
                    {/* Panel header with dropdown */}
                    <div className="px-2.5 py-2 border-b border-black/20 flex items-center gap-2 bg-white/80 shrink-0 flex-wrap">
                        <span className="text-xs text-coal/50 font-bold uppercase tracking-wide">Viewing</span>
                        <FeedDropdown activeFeedKey={activeFeedKey} onChange={setActiveFeedKey} />
                    </div>

                    {/* Feed panel */}
                    <div className="flex-1 flex flex-col min-h-0">
                        <UnifiedFeedPanel
                            key={activeFeedKey}
                            feedKey={activeFeedKey}
                            {...sharedFeedProps}
                        />
                    </div>

                    {/* New-activity banner - overlaid on top of the feed rows */}
                    {showNewBanner && (
                        <>
                            <style>{`
                                @keyframes frankly-glow {
                                    0%, 100% { box-shadow: 0 0 6px 2px rgba(251,191,36,0.35); }
                                    50%       { box-shadow: 0 0 16px 4px rgba(251,191,36,0.7); }
                                }
                                @keyframes frankly-banner-in {
                                    from { opacity: 0; transform: translateY(-6px); }
                                    to   { opacity: 1; transform: translateY(0); }
                                }
                                .frankly-banner {
                                    animation: frankly-banner-in 0.2s ease-out forwards, frankly-glow 2.4s ease-in-out infinite;
                                }
                            `}</style>
                            <button
                                type="button"
                                onClick={() => { setShowNewBanner(false); setActiveFeedKey("recent"); }}
                                className="frankly-banner absolute top-[41px] left-0 right-0 z-20 w-full flex items-center justify-center gap-2.5 px-4 py-2.5 bg-amber-400 border-b-2 border-amber-500 text-coal font-bold text-sm hover:bg-amber-300 transition-colors"
                                style={{ textShadow: "0 1px 0 rgba(255,255,255,0.35)" }}
                            >
                                <span>✦</span>
                                <span className="tracking-wide">New activity - tap to see the latest posts</span>
                                <span>✦</span>
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
                                    <path d="M5 12h14M12 5l7 7-7 7"/>
                                </svg>
                            </button>
                        </>
                    )}
                </div>

                {/* Right: Luv column */}
                <div style={{ height: 800 }} className="flex flex-col gap-3">
                    <div className="flex-1 min-h-0 flex flex-col">
                        <LuvColumn
                            ingestPosts={ingestPosts}
                            luvById={luvById}
                            setLuvById={setLuvById}
                            refreshSignal={luvRefreshSignal}
                            userData={userData}
                        />
                    </div>
                    <div className="h-[320px] shrink-0 flex flex-col">
                        <AppreciatedColumn />
                    </div>
                </div>

            </div>
        </div>

        {modalPost && (
            <PostThreadModal
                post={modalPost.post}
                profileUsername={modalPost.author}
                userData={userData}
                isLoggedIn={Boolean(userData)}
                isGuest={false}
                onClose={closeThread}
            />
        )}
        <ConfirmModal state={confirmState} onClose={() => setConfirmState(null)} />
        </>
    );
}

export default TopPosts;